import WriteButton from "@/components/WriteButton";
import PurchaseWorkspace from "@/components/PurchaseWorkspace";
import PurchaseHistoryDetail from "@/components/PurchaseHistoryDetail";
import { moneyText, importedPurchaseLine } from "@/lib/purchase-workspace.mjs";
import { useAuth } from "@/context/AuthContext";
import { purchaseDraftPrefix, readPurchaseDrafts, removePurchaseDraft, removeLocalPurchaseDraft } from "@/lib/purchase-drafts";
import { useCallback, useEffect, useState, useRef } from "react";
import { transactionPeriod } from '@/lib/transaction-period.mjs';
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { paymentLabel } from '@/lib/purchase-history.mjs';
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Plus, Trash2, ShoppingBag, ChevronRight, Upload, FileDown, FileSpreadsheet, Loader2,
} from "lucide-react";

const statusBadge = {
  lunas: "bg-brand-soft text-brand-readable",
  utang: "bg-warning-soft text-warning-readable",
  sebagian: "bg-warning-soft text-warning-readable",
};
const paymentTypeLabel = { tunai: "Tunai", transfer: "Transfer", utang: "Utang", campuran: "Campuran" };

export default function Purchases() {
  const { tenant, user, readOnly } = useAuth();
  const location = useLocation();
  const draftPrefix = purchaseDraftPrefix(tenant?.id, user?.id);
  const [drafts, setDrafts] = useState([]);
  const [draftError, setDraftError] = useState("");
  const [editingDraft, setEditingDraft] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [period, setPeriod] = useState(() => location.state?.openPurchaseId ? 'all' : 'today');
  const [customDates, setCustomDates] = useState(() => transactionPeriod('today'));
  const range = period === 'custom' ? customDates : transactionPeriod(period);
  const invalidPeriod = period === 'custom' && (!range.start || !range.end || range.start > range.end);
  const periodQuery = new URLSearchParams({ ...(range.start ? { from_date: range.start } : {}), ...(range.end ? { to_date: range.end } : {}) }).toString();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const loadSequence = useRef(0), loadedCount = useRef(0);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [importLines, setImportLines] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const refreshDrafts = async () => {
    if (readOnly || !tenant?.id || !user?.id) return;
    try {
      const result = await readPurchaseDrafts(draftPrefix);
      setDrafts(result.drafts); setDraftError(result.warning);
    } catch (e) { setDraftError(`Draf server belum dapat dimuat: ${apiError(e)}`); }
  };
  useEffect(() => {
    if (open) return;
    refreshDrafts();
    const changed = () => refreshDrafts();
    window.addEventListener("focus", changed);
    window.addEventListener("online", changed);
    return () => {
      window.removeEventListener("focus", changed);
      window.removeEventListener("online", changed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPrefix, open, readOnly]);
  const openNew = () => { setEditingDraft(null); setImportLines(null); setOpen(true); };
  const closeForm = () => { setOpen(false); setImportLines(null); setEditingDraft(null); };
  const deleteDraft = async (draft) => {
    if (!window.confirm("Hapus draf pembelian ini? Isian draf tidak dapat dikembalikan.")) return;
    try {
      if (draft.recoverySourceId) removeLocalPurchaseDraft(draftPrefix, draft.recoverySourceId);
      else await removePurchaseDraft(draft);
      await refreshDrafts();
    } catch (e) { toast.error(`Draf gagal dihapus: ${apiError(e)}`); }
  };

  const load = useCallback(async (more = false) => {
    const sequence = ++loadSequence.current;
    if (!more) { setPurchases([]); loadedCount.current = 0; setHasMore(false); }
    if (invalidPeriod) { setLoading(false); return; }
    setLoading(true); setLoadError('');
    try {
      const [pu, pr, su] = await Promise.all([
        api.get(`/purchases?${periodQuery}&limit=200&offset=${more ? loadedCount.current : 0}`), api.get("/products"), api.get("/suppliers"),
      ]);
      if (sequence !== loadSequence.current) return;
      setPurchases(previous => more ? [...previous, ...pu.data] : pu.data);
      loadedCount.current = (more ? loadedCount.current : 0) + pu.data.length;
      setHasMore(pu.data.length === 200);
      setProducts(pr.data);
      setSuppliers(su.data);
    } catch (e) { if (sequence === loadSequence.current) { setLoadError(apiError(e)); toast.error(apiError(e)); } }
    finally { if (sequence === loadSequence.current) setLoading(false); }
  }, [periodQuery, invalidPeriod]);
  useEffect(() => {
    const requestCounter = loadSequence;
    load();
    return () => { requestCounter.current++; };
  }, [load]);
  const paymentIds = purchases.filter(p => p.payment_managed_by === 'akunkas').map(p => p.id).join(',');
  useEffect(() => {
    if (open || !paymentIds) return;
    let stopped = false, running = false;
    const refreshPayments = async () => {
      if (running || document.hidden || !navigator.onLine) return;
      running = true;
      try {
        const ids = paymentIds.split(',');
        for (let i = 0; i < ids.length && !stopped; i += 100) {
          const { data } = await api.get('/purchases/payment-status', { params: { ids: ids.slice(i, i + 100).join(',') } });
          if (stopped) return;
          const patches = new Map(data.map(p => [p.id, p]));
          setPurchases(rows => rows.map(p => patches.has(p.id) ? { ...p, ...patches.get(p.id) } : p));
          setSelected(p => p && patches.has(p.id) ? { ...p, ...patches.get(p.id) } : p);
        }
      } catch { /* Existing verification time remains visible; never invent payment success. */ }
      finally { running = false; }
    };
    const timer = setInterval(refreshPayments, 30000);
    window.addEventListener('focus', refreshPayments);
    window.addEventListener('online', refreshPayments);
    return () => { stopped = true; clearInterval(timer); window.removeEventListener('focus', refreshPayments); window.removeEventListener('online', refreshPayments); };
  }, [paymentIds, open]);
  useEffect(() => {
    const openId = location.state?.openPurchaseId;
    if (!openId || !purchases.length) return;
    const found = purchases.find((p) => p.id === openId);
    if (found) setSelected(found);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, purchases]);

  useEffect(() => {
    const id = location.state?.openDraftId;
    if (!id || open) return;
    const draft = drafts.find(d => d.id === id);
    if (!draft) return;
    setEditingDraft(draft); setImportLines(null); setOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, drafts, open, navigate]);

  const setStatus = async (p, status) => {
    try {
      const { data } = await api.put(`/purchases/${p.id}/payment?status=${status}`);
      toast.success("Status pembayaran diperbarui");
      load();
      setSelected((s) => (s && s.id === p.id ? data : s));
    } catch (e) { toast.error(apiError(e)); }
  };
  const applyReturnedPurchase = (updated) => {
    setSelected(updated);
    setPurchases((current) => current.map((purchase) => purchase.id === updated.id ? updated : purchase));
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/purchases/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`${data.errors.length} baris dilewati: ${data.errors.slice(0, 3).join('; ')}${data.errors.length > 3 ? '; periksa baris lainnya di Excel' : ''}`, { duration: 12000 });
      if (!data.items?.length) { toast.error("Tidak ada baris valid pada file Excel"); return; }
      const lines = data.items.map(importedPurchaseLine);
      setImportLines(lines);
      setEditingDraft(null);
      setOpen(true);
      toast.success(`${lines.length} barang dimuat dari Excel — lengkapi supplier lalu simpan`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  return (
    <>
      <PageHeader title="Pembelian Barang" subtitle={`${purchases.length} faktur pembelian`}>
        <div className="flex items-center gap-2">
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
          <Button data-testid="download-template-btn" variant="outline" onClick={() => downloadFile("/purchases/template", "template_pembelian.xlsx")} className="border-ui-line bg-transparent hover:bg-ui-card gap-2">
            <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">Template</span>
          </Button>
          <WriteButton data-testid="import-excel-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-brand/50 bg-transparent hover:bg-brand-soft text-brand-readable gap-2">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} <span className="hidden sm:inline">Import Excel</span>
          </WriteButton>
          <Button data-testid="export-purchases-btn" variant="outline" disabled={invalidPeriod} onClick={() => downloadFile(`/purchases/export?${periodQuery}`, "riwayat_pembelian.xlsx")} className="border-ui-line bg-transparent hover:bg-ui-card gap-2">
            <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">Export</span>
          </Button>
          <WriteButton data-testid="add-purchase-btn" onClick={openNew} className="bg-brand-button hover:bg-brand-button text-ui-text font-bold gap-2">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Input Pembelian</span>
          </WriteButton>
        </div>
      </PageHeader>

      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5">
        {!readOnly && <section data-testid="purchase-drafts" className="mb-5 rounded-xl border border-ui-line bg-ui-card p-4 space-y-3">
          <div className="font-semibold">Draf Pembelian ({drafts.length})</div>
          <p className="text-xs text-ui-muted">Draf tersimpan di server dan dapat dilanjutkan dari browser atau perangkat lain dengan akun toko yang sama. Stok belum berubah sebelum Simpan Pembelian.</p>
          {draftError && <p role="alert" className="text-sm text-danger-readable">{draftError}</p>}
          {!drafts.length && <p className="text-sm text-ui-muted">Belum ada draf pembelian.</p>}
          {drafts.map((draft) => <div key={draft.id} data-testid={`purchase-draft-${draft.id}`} className="flex flex-wrap items-center justify-between gap-3 border-t border-ui-line pt-3">
            <div className="min-w-0">
              <div className="font-medium break-words">{draft.supplierName || "Supplier belum diisi"}{draft.invoiceNo ? ` · ${draft.invoiceNo}` : ""}</div>
              {!!draft.warehouse_receipt_ids?.length && <div className="text-xs text-brand-readable">Dari Penerimaan Gudang · tertaut, tidak dibuat ganda</div>}
              {draft.destinationStoreName && <div className="text-xs text-brand-readable">Tujuan: {draft.destinationStoreName}</div>}
              {draft.recoverySourceId && <div className="text-xs text-warning-readable">Salinan pemulihan dari perangkat ini — belum tersimpan di server</div>}
              <div className="text-xs text-ui-muted">{draft.lines.length} barang · {draft.date} · {draft.schemaVersion === 2 ? "Rincian total tersedia saat dibuka" : rupiah(draft.lines.reduce((sum, line) => sum + Number(line.qty || 0) * Number(line.cost || 0), 0))}</div>
            </div>
            <div className="flex gap-2">
              <WriteButton data-testid={`resume-purchase-draft-${draft.id}`} variant="outline" onClick={() => { setImportLines(null); setEditingDraft(draft); setOpen(true); }}>Lanjutkan Draf</WriteButton>
              {!draft.warehouse_receipt_ids?.length && <WriteButton data-testid={`delete-purchase-draft-${draft.id}`} variant="outline" onClick={() => deleteDraft(draft)} aria-label="Hapus draf pembelian"><Trash2 className="h-4 w-4" /></WriteButton>}
            </div>
          </div>)}
        </section>}
        <section aria-label="Periode pembelian" className="mb-3 flex flex-wrap items-end gap-2">
          <label className="text-xs text-ui-muted">Periode
            <select aria-label="Periode pembelian" value={period} onChange={event => setPeriod(event.target.value)} className="block h-10 mt-1 rounded-md border border-ui-line bg-ui-card text-ui-text px-3">
              <option value="today">Hari ini</option><option value="yesterday">Kemarin</option><option value="this-month">Bulan ini</option><option value="all">Semua</option><option value="custom">Tanggal sendiri</option>
            </select>
          </label>
          {period === 'custom' && <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
            <label className="text-xs text-ui-muted min-w-0">Dari<input aria-label="Tanggal awal pembelian" type="date" value={customDates.start} max={customDates.end || undefined} onChange={event => setCustomDates(value => ({ ...value, start: event.target.value }))} className="block mt-1 h-10 w-full min-w-0 rounded-md border border-ui-line bg-ui-card text-ui-text px-2" /></label>
            <label className="text-xs text-ui-muted min-w-0">Sampai<input aria-label="Tanggal akhir pembelian" type="date" value={customDates.end} min={customDates.start || undefined} onChange={event => setCustomDates(value => ({ ...value, end: event.target.value }))} className="block mt-1 h-10 w-full min-w-0 rounded-md border border-ui-line bg-ui-card text-ui-text px-2" /></label>
          </div>}
          <span role="status" className="text-xs text-ui-muted pb-2">{loading ? 'Memuat…' : `${purchases.length} faktur ditampilkan`} · tanggal faktur (WIB)</span>
          {invalidPeriod && <p role="alert" className="w-full text-xs text-danger-readable">Isi rentang tanggal yang valid; tanggal awal tidak boleh setelah tanggal akhir.</p>}
          {loadError && <p role="alert" className="w-full text-xs text-danger-readable">{loadError} <button type="button" onClick={() => load()} className="underline">Coba lagi</button></p>}
        </section>
        <div className="rounded-xl border border-ui-line overflow-x-auto" data-testid="purchases-table">
          <table className="w-full min-w-[540px] text-sm">
            <thead className="bg-ui-card text-ui-muted text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">No. Faktur</th>
                <th className="text-left p-3">Supplier</th>
                <th className="text-left p-3 hidden md:table-cell">Tanggal</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Total</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} data-testid={`purchase-row-${p.id}`} onClick={() => setSelected(p)} className="border-t border-ui-line hover:bg-ui-soft cursor-pointer">
                  <td className="p-3 font-mono">{p.invoice_no}<div className="md:hidden text-[10px] text-ui-muted">{p.date}</div></td>
                  <td className="p-3">{p.supplier_name}</td>
                  <td className="p-3 hidden md:table-cell text-ui-muted">{p.date}</td>
                  <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full ${statusBadge[p.payment_status] || statusBadge.utang}`}>{p.payment_managed_by === 'akunkas' ? paymentLabel(p) : paymentTypeLabel[p.payment_type] || p.payment_status}</span>{p.payment_status === 'sebagian' && <div className="text-[10px] text-ui-muted mt-1">Sebagian utang</div>}{p.return_total > 0 && <div className="text-[10px] text-danger-readable mt-1">RETUR {rupiah(p.return_total)}</div>}</td>
                  <td className="p-3 text-right font-mono font-bold text-accent-readable">{p.calculation ? moneyText(p.net_total) : rupiah(p.net_total)}{p.return_total > 0 && <div className="text-[10px] font-normal text-ui-muted line-through">{rupiah(p.total)}</div>}</td>
                  <td className="p-3 text-ui-muted"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
              {purchases.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-ui-muted"><ShoppingBag className="h-8 w-8 mx-auto mb-2" /> {loading ? 'Memuat pembelian…' : invalidPeriod ? 'Periksa rentang tanggal' : loadError ? 'Data gagal dimuat' : 'Tidak ada pembelian pada periode ini'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {hasMore && <div className="mt-3 text-center"><Button variant="outline" disabled={loading} onClick={() => load(true)}>Muat lebih banyak</Button></div>}
      </div>

      {open && <PurchaseWorkspace onClose={closeForm} initialLines={importLines} initialDraft={editingDraft} draftPrefix={draftPrefix} products={products} suppliers={suppliers} onSupplierCreated={(supplier) => setSuppliers((previous) => [...previous, supplier].sort((a, b) => a.name.localeCompare(b.name)))} onSaved={load} />}

      {selected && <PurchaseHistoryDetail purchase={selected} products={products} onClose={() => setSelected(null)} onStatusChange={setStatus} onReturned={applyReturnedPurchase} readOnly={readOnly} />}

    </>
  );
}
